import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { TranslateModule } from '@ngx-translate/core';
import { EMPTY, filter, switchMap, tap, delay } from 'rxjs';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';

import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';

import { BaseDrawerComponent } from '../base-drawer.component';
import { AsyncPipe } from '@angular/common';

import { NavigationBlockItemComponent } from './navigation-block-item';

import { NavigationDrawerService } from './navigation-drawer.service';

@Component({
    selector: 'web-navigation-drawer',
    templateUrl: './navigation-drawer.component.html',
    styleUrls: ['./navigation-drawer.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('slideIn', [
            transition(':enter', [
                style({ transform: 'translate3d(-100%, 0, 0)' }),
                animate('200ms', style({ transform: 'translate3d(0, 0, 0)' })),
            ]),
            transition(':leave', [
                style({ transform: 'translate3d(0, 0, 0)' }),
                animate('200ms', style({ transform: 'translate3d(-100%, 0, 0)' })),
            ]),
        ]),
    ],
    imports: [NavigationBlockItemComponent, AsyncPipe, TranslateModule],
})
export class NavigationDrawerComponent extends BaseDrawerComponent implements OnInit {
    private readonly blocksFacade: BlocksFacade = inject(BlocksFacade);
    private readonly navDrawerService: NavigationDrawerService = inject(NavigationDrawerService);

    public readonly showDrawer$ = this.layoutFacade.showNavigationDrawer$;

    public readonly blocks = toSignal(this.blocksFacade.blocks$, { initialValue: [] });
    public readonly selectedEntity = toSignal(this.layoutFacade.selectedEntity$);

    private readonly target$ = toObservable(this.navDrawerService.target);

    public ngOnInit(): void {
        this.showDrawer$
            .pipe(
                switchMap((isOpen) => {
                    if (!isOpen) {
                        // Drop the last target so reopening the drawer without a fresh
                        // selection doesn't replay a stale scroll/expand via toObservable.
                        this.navDrawerService.clearTarget();
                        return EMPTY;
                    }

                    return this.target$.pipe(filter(Boolean));
                }),
                tap((target) => {
                    if (target.blockId) {
                        this.navDrawerService.addBlockId(target.blockId);
                    }

                    if (target.sectionId) {
                        this.navDrawerService.addRowId(target.sectionId);
                    }

                    if (target.columnId) {
                        this.navDrawerService.addColumnId(target.columnId);
                    }

                    if (target.gridId) {
                        this.navDrawerService.addGridId(target.gridId);
                    }
                }),
                delay(300),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((target) => {
                const element = document.getElementById('nav-' + target.entityId);
                if (!element) {
                    console.warn('Element not found. ID =', target.entityId);
                    return;
                }
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
    }
}
